// src/oura/places.ts
//
// The three places a customer can find a business, as ONE shape both roles
// read: Google search (the links and the map), Google's AI answer (the
// paragraph above the links) and the AI assistants (ChatGPT, Gemini).
//
// Read from the blocks the backend already ships (`searchPerformance`,
// `aiVisibility`, `grounding`); a sample business passes payload-shaped
// fixtures through the same reader. Nothing here invents a number: a place
// that was not measured says so, and Google's AI answer is unmeasured
// everywhere today. The BE ships it as `engines[Google AI].probed = false`,
// and a rendered 0 there would read as a measurement nobody made.

import type {
    AeoAiVisibility,
    AeoGrounding,
    AeoSearchPerformance,
} from "@service/aeo";
import { ENGINE_LABEL } from "./lib/oura";
import { instrumentColumnLabel } from "./components/aeo/InstrumentBadge";
import { deltaCrossesBreak, trendBreakIndex, type TrendBreak } from "./trendBreak";

export interface PlaceGoogle {
    connected: boolean;
    /** Best organic position across the tracked questions, when known. */
    bestPosition: number | null;
    top3: number | null;
    top10: number | null;
    /** The third band. Measured, and never shown until now. */
    top20: number | null;
    /**
     * How many non-branded searches the site appears for, IN TOTAL.
     *
     * NOT the length of the keyword table. That list is capped at 50, and
     * using it as the denominator said "6 of 50 in the top 3" where the truth
     * was 6 of 185 — a smaller denominator makes the same win look bigger,
     * which is the direction a number must never be wrong in. The bands are
     * cumulative, so the total is `top20 + beyond20`.
     */
    keywords: number | null;
    avgPosition: number | null;
    clicks: number | null;
    impressions: number | null;
}

/**
 * What the AI Overviews row says, or `null` when the payload does not mention
 * it at all.
 *
 * NULL IS NOT "NOT MEASURED". The backend dropped the placeholder row on
 * purpose (ourteam-backend#5032, #5033 — "drop the placeholder row rather than
 * print it for another year"), and an engine the payload no longer names is not
 * an engine we are "not yet" measuring: it is one that is not in this product
 * right now. The card renders nothing for it (ourteam-frontend#3909).
 */
export type PlaceGoogleAi =
    | { measured: false; reason: string }
    | { measured: true; named: number; of: number }
    | null;

export interface PlaceEngine {
    key: string;
    label: string;
    named: number;
    of: number;
    /** `app` is the consumer ChatGPT app, the instrument a client can check.
     *  `api` reads lower and carries no location. */
    instrument: "app" | "api" | "unknown";
    /** Set when the engine was never asked on this scan: the reason, in words. */
    skipped?: string | null;
}

export interface PlaceAssistants {
    /** Questions on which at least one assistant named the business. */
    named: number;
    /** Winnable questions: asked, less the ones no business could win. */
    of: number;
    asked: number;
    excluded: number;
    engines: PlaceEngine[];
    weakest: PlaceEngine | null;
    grounding: AeoGrounding | null;
    /** Why there is no assistants figure, in words, when there is none. */
    absentReason: string | null;
    /** Movement since the previous scan, when there was one. `neutral` when
     *  the question set changed in between, so the two points do not compare. */
    delta: { text: string; up: boolean; neutral?: boolean } | null;
}

/** One place an assistant reads about the business: a directory, the map, a
 *  review site, or the business's own pages. */
export interface PlaceSource {
    name: string;
    note: string | null;
    status: "ok" | "warn" | "missing" | "count";
    count?: number;
    own?: boolean;
}

export interface PlaceSources {
    /**
     * Share of the answers that CAN cite a source which cite this business's
     * own site — `aiVisibility.citationShare`, measured by the backend.
     *
     * Never `ownDomainAnswers / citations`: those two count the same thing, so
     * the division is 1 whenever there is anything to divide and `null`
     * otherwise (#3845).
     */
    ownShare: number | null;
    ownAnswers: number | null;
    citedAnswers: number | null;
    /** Which engines the share covers. Absent on scans before the backend sent
     *  it, and on a ChatGPT-app run the app itself renders no links. */
    shareEngines: string[] | null;
    /** What the share counts — questions of the set, or answers (#3890).
     *  Null on a scan from before the backend said, which counted answers. */
    shareBasis: string | null;
    /** The same question, for the per-domain counts. */
    domainsBasis: string | null;
    sources: PlaceSource[];
    absentReason: string | null;
    /** Rented visibility: the mentions come from directories and the map, which
     *  caps the rating at 3 of 5 whatever the mention rate (YW, 4 Sep 2026). */
    capped: boolean;
}

export interface Places {
    google: PlaceGoogle;
    googleAi: PlaceGoogleAi;
    assistants: PlaceAssistants;
    sources: PlaceSources;
}

/** The backend's cap threshold (`RENTED_VISIBILITY_CAP_BELOW`). */
export const RENTED_VISIBILITY_CAP_BELOW = 0.2;

const NO_GOOGLE: PlaceGoogle = {
    connected: false, bestPosition: null, top3: null, top10: null, top20: null,
    keywords: null, avgPosition: null, clicks: null, impressions: null,
};

function weakestOf(engines: PlaceEngine[]): PlaceEngine | null {
    const rated = engines.filter((e) => e.of > 0 && !e.skipped);
    if (rated.length < 2) return null;
    return rated.reduce((a, b) => (b.named / b.of < a.named / a.of ? b : a));
}

/* ------------------------------------------------------------------------- *
 * Live: the payload blocks, read as they are shipped.
 * ------------------------------------------------------------------------- */

/**
 * The engine's display name, never its raw key (#3537, #3752). The ChatGPT
 * column takes its name off the run's `instrumentLabel`, the way the Visibility
 * tab does, so a run that fell back to the API reads "OpenAI" here too. Other
 * keys go through the FE's own engine map, then the labels the payload's
 * `engines[]` block carries; a key nothing recognises reads "Engine", not the key.
 */
/**
 * Which ENGINE a row is about, regardless of the instrument that answered it.
 *
 * "ChatGPT (app)" and "OpenAI (API)" are one engine asked two ways. Keying a
 * row on its label instead put both on screen at once: ownlab showed a measured
 * OpenAI ring beside a dashed OpenAI ring, the second being the app attempt
 * that failed. Two rings, one engine, and the same name under each.
 */
export function engineIdentity(key: string): string {
    const k = key.toLowerCase();
    if (k.includes("chatgpt") || k.includes("openai")) return "chatgpt";
    if (k.includes("gemini") || k.includes("google ai")) return k.includes("google ai") ? "google_ai" : "gemini";
    return k.split(" ")[0];
}

function engineDisplay(
    key: string,
    vis: AeoAiVisibility | null | undefined,
    grounding: AeoGrounding | null | undefined,
): { label: string; instrument: PlaceEngine["instrument"] } {
    const k = key.toLowerCase();
    if (k.includes("chatgpt")) return { label: instrumentColumnLabel(grounding, ENGINE_LABEL.chatgpt), instrument: "app" };
    if (k.includes("openai")) return { label: instrumentColumnLabel(grounding, "OpenAI"), instrument: "api" };
    if (ENGINE_LABEL[k]) return { label: ENGINE_LABEL[k], instrument: "api" };
    const shipped = (vis?.engines ?? []).find((e) => e.engine.toLowerCase().startsWith(k.split("_")[0]));
    return { label: shipped?.engine.split("(")[0].trim() || "Engine", instrument: "unknown" };
}

/**
 * The reason an engine was not asked, as a sentence a client can read.
 *
 * The backend's detail can be a raw traceback — "RuntimeError: no question
 * could be answered in the browser. First: abandoned: the browser answered
 * nothing; abandoned: the browser answered nothing" — and that ran across a
 * card a merchant reads. The first sentence carries the fact; the stack stays
 * in the payload for whoever needs it.
 */
function skipSentence(detail: string | null | undefined): string {
    const raw = (detail ?? "").trim();
    if (!raw) return "not asked on this scan";
    // Drop the exception class and anything after the first sentence.
    const withoutClass = raw.replace(/^[A-Za-z.]*(Error|Exception):\s*/, "");
    const first = withoutClass.split(/(?<=[.!?])\s/)[0].trim();
    return first.length > 120 ? `${first.slice(0, 117).trimEnd()}…` : first;
}

export function placesFromLive(
    sp: AeoSearchPerformance | null | undefined,
    vis: AeoAiVisibility | null | undefined,
    grounding: AeoGrounding | null | undefined,
    aeoScoreAbsent?: string | null,
    trendBreak?: TrendBreak | null,
): Places {
    const google: PlaceGoogle = sp?.connected
        ? {
            connected: true,
            // Search Console reports an average, never a best spot; the tile
            // labels whichever it has.
            bestPosition: null,
            top3: sp.buckets?.top3 ?? null,
            top10: sp.buckets?.top10 ?? null,
            top20: sp.buckets?.top20 ?? null,
            // The bands are CUMULATIVE, so everything the site appears for is
            // `top20 + beyond20`. The keyword table is capped at 50 and is a
            // sample, not a count — it stands in only for a payload with no
            // buckets at all.
            keywords: sp.buckets
                ? sp.buckets.top20 + sp.buckets.beyond20
                : sp.keywords?.length ?? null,
            avgPosition: sp.totals?.avgPosition ?? null,
            clicks: sp.totals?.clicks ?? null,
            impressions: sp.totals?.impressions ?? null,
        }
        : NO_GOOGLE;

    // Absent row and present-but-unprobed row are DIFFERENT, and collapsing
    // them is what kept a block on screen that the backend had already removed.
    const googleAiShare = vis?.engines.find((e) => e.engine === "Google AI");
    const googleAi: PlaceGoogleAi = !googleAiShare
        ? null
        : googleAiShare.probed
            // Pages still being read are not in the denominator: they are
            // neither named nor missed yet (ourteam-backend#5619).
            ? { measured: true, named: googleAiShare.mentions, of: Math.max(0, (vis?.coverage?.questionsAsked ?? 0) - (googleAiShare.coverage?.pending ?? 0)) }
            // The row exists and says it was not probed, so it is entitled to
            // say why — in its own words, and ONLY in its own words. A copy of
            // the backend's sentence used to sit here as a fallback; it fires
            // on none of the twelve rows staging carries, which is the whole
            // problem with it: a duplicate that never runs is a duplicate that
            // diverges silently the day the backend rewords. A row with nothing
            // to say says nothing, and the block's own sentence still explains
            // what the surface is.
            : { measured: false, reason: googleAiShare.summary || "" };

    const asked = vis?.coverage?.questionsAsked ?? 0;
    const excluded = (vis?.coverage?.headlineExcluded ?? 0) + (vis?.coverage?.brandedExcluded ?? 0);
    const of = Math.max(0, asked - excluded);
    const rates = vis?.engineRates ?? {};
    const engines: PlaceEngine[] = Object.entries(rates).map(([key, r]) => ({
        key, ...engineDisplay(key, vis, grounding), named: r.mentioned, of: r.answered,
    }));
    // An engine that was never asked is a row that says so, not a missing row:
    // "measured on one engine" is a different claim from "cross-checked".
    //
    // Matched on the ENGINE, not on the label. A failed ChatGPT-app attempt
    // beside a measured OpenAI-API answer is ONE engine that was asked twice —
    // the instrument badge above already says which way it answered — and
    // listing both drew a dashed empty ring under the same name as the
    // measured one.
    for (const e of vis?.engines ?? []) {
        const identity = engineIdentity(e.engine);
        if (e.probed === false && identity !== "google_ai" && !e.citationOnly
            && !engines.some((x) => engineIdentity(x.key) === identity)) {
            engines.push({
                key: e.engine, ...engineDisplay(e.engine, vis, grounding), named: 0, of: 0,
                skipped: skipSentence(e.skipDetail || e.summary),
            });
        }
    }
    // Distinct questions named across engines, when the backend ships it; until
    // then the client-facing instrument's own count is the honest stand-in and
    // never overstates.
    const app = engines.find((e) => e.instrument === "app");
    const named = vis?.questionsNamed ?? app?.named ?? Math.max(0, ...engines.map((e) => e.named));
    // Mentions, not questions: that is the series the backend keeps per scan.
    // A delta across a re-authored question set compares two rulers (#3539):
    // it is shown as a break, never as a rise or a fall.
    const dp = vis?.mentionsDeltaPct;
    const crossesBreak = deltaCrossesBreak(vis?.trend, trendBreakIndex(vis?.trend, vis?.trendCapturedAt, trendBreak));
    const delta = crossesBreak
        ? { text: "question set changed since last scan, no comparison", up: false, neutral: true }
        : typeof dp === "number" && dp !== 0
            ? { text: `${dp > 0 ? "+" : ""}${Math.round(dp)}% mentions since last scan`, up: dp > 0 }
            : null;
    /**
     * The measured share, or nothing.
     *
     * `citationShare` is the backend's own division (#4868): `count` answers
     * out of the `denominator` answers that could carry a source at all. The
     * old arithmetic here divided `ownDomainAnswers` by `citations`, which are
     * two names for the same number — so every business with any citations read
     * 100%, and every business with none read "Not measured" over a real,
     * actionable zero (#3845).
     *
     * The pre-#4868 fields are kept as the fallback for stored scans that
     * predate the block, and only there.
     */
    const share = vis?.citationShare ?? null;
    const measuredShare = share && !share.absent && share.denominator > 0;
    const ownAnswers = measuredShare ? share.count : null;
    const citedAnswers = measuredShare ? share.denominator : null;
    const ownShare = measuredShare
        ? (typeof share.share === "number" ? share.share : share.count / share.denominator)
        : null;
    // A scan stored before #4868 has no denominator anywhere, so there is no
    // share to state — and stating the old one would repeat the 100% this
    // ticket exists to remove. The COUNT it does have is real and is kept:
    // "23 answers cite this business's own site" claims nothing about the rest.
    const legacyOwn = share ? null : vis?.ownDomainAnswers ?? null;
    const legacyCited = null;
    const sources: PlaceSources = {
        ownShare,
        ownAnswers: ownAnswers ?? legacyOwn,
        citedAnswers: citedAnswers ?? legacyCited,
        shareEngines: share?.engines?.length ? share.engines : null,
        shareBasis: share?.basis ?? null,
        domainsBasis: vis?.sourceDomainsBasis ?? null,
        sources: (vis?.sourceDomains ?? []).slice(0, 5).map((d) => ({
            name: d.domain, note: null, status: "count", count: d.answers, own: d.own,
        })),
        // Only a stated absence counts as "not measured". A zero share is a
        // measurement and says so on the card.
        absentReason: share?.absent
            ? share.absent
            : vis?.citationsAbsent && !share
                ? "The ChatGPT app renders no links; the link figures come from the API pass."
                : null,
        capped: ownShare !== null && ownShare < RENTED_VISIBILITY_CAP_BELOW && named > 0,
    };
    return {
        google,
        googleAi,
        assistants: {
            named, of, asked, excluded, engines,
            weakest: weakestOf(engines), grounding: grounding ?? null,
            absentReason: of > 0 ? null : absenceText(vis?.scoreAbsent ?? aeoScoreAbsent ?? null),
            delta,
        },
        sources,
    };
}

/** The backend's absence codes (#4096), in the operator's words. `not_probed`
 *  and `insufficient_sample` are different facts with different fixes. */
export function absenceText(code: string | null): string {
    switch (code) {
        case null: return "No questions were scored on this scan.";
        case "not_probed": return "No engine was asked on this scan.";
        case "insufficient_sample": return "Too few answers came back to score this honestly.";
        case "not_reported": return "The engines answered, but no result was reported for this scan.";
        case "no_mentions": return "The engines answered and named no business at all.";
        case "not_extracted": return "The answers came back, but nothing could be read out of them.";
        default: return `Not scored: ${code.replace(/_/g, " ")}.`;
    }
}
